/**
 * components/landing/home/architecture/PreviewPanel.jsx — the right rail
 * of the Interactive Platform Architecture.
 *
 * Renders a "live" mini-dashboard for the currently-selected module.
 * When no module is selected, we cycle through subtle summary cards
 * so the panel never looks empty.
 *
 * The chrome (top bar + status pill) is provided here so every preview
 * feels like part of the same product family.
 */

import React from 'react'
import clsx from 'clsx'
import { CircleDot, Layers, Rocket } from 'lucide-react'
import { getModule, TONE_COLORS, VIEW_MODES } from './data'
import { PREVIEWS } from './previews'

export default function PreviewPanel({ activeId, viewMode }) {
  const active = activeId ? getModule(activeId) : null
  const tone   = TONE_COLORS[active?.tone || 'orange']
  const Preview = active ? (PREVIEWS[active.preview] || null) : null

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-white/[0.01] p-4 md:p-5 backdrop-blur-xl h-full">
      {/* Window chrome */}
      <div className="flex items-center gap-1.5 rounded-md bg-black/40 px-2.5 py-2 border border-white/5">
        <div className="h-2 w-2 rounded-full bg-red-500/70" />
        <div className="h-2 w-2 rounded-full bg-amber-500/70" />
        <div className="h-2 w-2 rounded-full bg-emerald-500/70" />
        <div className="ml-3 flex-1 flex items-center h-5 rounded-md bg-white/[0.04] px-2 text-[10px] font-mono text-zinc-500">
          {active ? `app.kalkiai.trade/${active.id}` : 'app.kalkiai.trade'}
        </div>
        <div className="flex items-center gap-1 text-[10px] font-mono text-emerald-400 pr-1">
          <CircleDot className="h-2.5 w-2.5 animate-pulse" /> live
        </div>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">Live module preview</span>
          <h4 className="text-lg font-semibold text-white leading-tight">
            {active ? active.name : viewMode.label}
          </h4>
        </div>
        <span
          className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider"
          style={{ borderColor: `rgba(${tone.rgb},0.35)`, color: tone.hex, background: `rgba(${tone.rgb},0.08)` }}
        >
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: tone.hex }} />
          {active ? (active.ring === 'inner' ? 'Core' : 'Platform') : 'Overview'}
        </span>
      </div>

      {/* Live preview OR fallback */}
      {Preview ? (
        <Preview />
      ) : (
        <FallbackTiles viewMode={viewMode} />
      )}

      {/* Tiny footer note */}
      <div className="pt-1 border-t border-white/5 flex items-center justify-between text-[10px] font-mono text-zinc-500">
        <span>{active ? `Module · ${active.id}` : 'Preview · overview'}</span>
        <span>kalkiai.trade</span>
      </div>
    </div>
  )
}

// ── Overview mode fallback tiles — shows a snapshot of the whole platform.
function FallbackTiles({ viewMode }) {
  const stats = [
    { k: 'Modules',       v: '18',   d: 'unified in one platform', tone: 'orange' },
    { k: 'Live tenants',  v: '4',    d: 'production white-labels', tone: 'green' },
    { k: 'Uptime · 90d',  v: '99.98%', d: 'multi-region SLA',       tone: 'blue' },
    { k: 'p99 · API',     v: '84ms', d: 'across all endpoints',    tone: 'purple' },
  ]
  return (
    <div className="flex-1 flex flex-col gap-3">
      <div className="rounded-xl border border-white/10 bg-black/40 p-3">
        <div className="flex items-center gap-2 text-xs text-zinc-300 mb-2">
          <Layers className="h-3.5 w-3.5 text-brand-gold" /> Platform snapshot
        </div>
        <div className="grid grid-cols-2 gap-2">
          {stats.map((s) => {
            const t = TONE_COLORS[s.tone]
            return (
              <div key={s.k} className="rounded-md border border-white/10 bg-white/[0.02] p-2.5">
                <div className="text-[9px] uppercase tracking-wider font-mono" style={{ color: t.hex }}>{s.k}</div>
                <div className="text-lg font-semibold text-white leading-tight">{s.v}</div>
                <div className="text-[10px] text-zinc-500">{s.d}</div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-black/40 p-3">
        <div className="flex items-center gap-2 text-xs text-zinc-300 mb-2">
          <Rocket className="h-3.5 w-3.5 text-brand-gold" /> {viewMode.label}
        </div>
        <p className="text-[12.5px] text-zinc-400 leading-relaxed">
          {viewMode.description} Click any node in the architecture to explore how each module contributes to the flow.
        </p>
      </div>
    </div>
  )
}
